import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Box,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
} from "@mui/material";
import {
  Visibility as ObservationIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Assignment as AssignmentIcon,
  Description as DescriptionIcon,
  AttachFile as AttachFileIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Star as StarIcon,
  TipsAndUpdates as TipsIcon,
  Flag as FlagIcon,
  Category as CategoryIcon,
  RateReview as RateReviewIcon,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useTheme, alpha } from "@mui/material/styles";

const ObservationGuideDialog = ({ open, onClose }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          maxHeight: "90vh",
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
          color: theme.palette.error.contrastText,
          fontWeight: 700,
          fontSize: "1.5rem",
          py: 3,
          px: 4,
          position: "relative",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.error.contrastText, 0.15),
              border: `2px solid ${alpha(theme.palette.error.contrastText, 0.3)}`,
            }}
          >
            <ObservationIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
              {t("observationGuide.title")}
            </Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
              {t("observationGuide.subtitle")}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            color: theme.palette.error.contrastText,
            "&:hover": {
              bgcolor: alpha(theme.palette.error.contrastText, 0.1),
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 4 }}>
          {/* Overview Section */}
          <Box
            sx={{
              mb: 3,
              p: 3,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.error.main, 0.05),
              border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                mb: 1,
                color: "error.main",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <ObservationIcon /> {t("observationGuide.overviewTitle")}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {t("observationGuide.overviewText")}
            </Typography>
          </Box>

          {/* Section 1: Understanding the Observation Form */}
          <Accordion
            defaultExpanded
            sx={{
              mb: 2,
              boxShadow: 1,
              "&:before": { display: "none" },
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08) },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <CategoryIcon sx={{ color: "primary.main" }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("observationGuide.section1Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {t("observationGuide.section1Text")}
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="1" size="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("observationGuide.section1Point1")}
                    secondary={t("observationGuide.section1Point1Desc")}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="2" size="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("observationGuide.section1Point2")}
                    secondary={t("observationGuide.section1Point2Desc")}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="3" size="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("observationGuide.section1Point3")}
                    secondary={t("observationGuide.section1Point3Desc")}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="4" size="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("observationGuide.section1Point4")}
                    secondary={t("observationGuide.section1Point4Desc")}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="5" size="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("observationGuide.section1Point5")}
                    secondary={t("observationGuide.section1Point5Desc")}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          {/* Section 2: Step 1 - Basic Information */}
          <Accordion
            sx={{
              mb: 2,
              boxShadow: 1,
              "&:before": { display: "none" },
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08) },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <AssignmentIcon sx={{ color: "primary.main" }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("observationGuide.section2Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {t("observationGuide.section2Text")}
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <FlagIcon sx={{ color: "primary.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("observationGuide.section2Step1")}
                    secondary={t("observationGuide.section2Step1Desc")}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CategoryIcon sx={{ color: "primary.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("observationGuide.section2Step2")}
                    secondary={t("observationGuide.section2Step2Desc")}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <WarningIcon sx={{ color: "primary.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("observationGuide.section2Step3")}
                    secondary={t("observationGuide.section2Step3Desc")}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItem>
              </List>
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 1,
                  bgcolor: alpha(theme.palette.info.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <TipsIcon sx={{ fontSize: 20, color: "info.main" }} />
                  {t("observationGuide.proTip")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mt: 1, color: "text.secondary" }}
                >
                  {t("observationGuide.section2ProTip")}
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Section 3: Step 2 - Description */}
          <Accordion
            sx={{
              mb: 2,
              boxShadow: 1,
              "&:before": { display: "none" },
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08) },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <DescriptionIcon sx={{ color: "primary.main" }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("observationGuide.section3Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {t("observationGuide.section3Text")}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                {t("observationGuide.section3BestPracticesTitle")}:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon
                      sx={{ color: "success.main", fontSize: 20 }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("observationGuide.section3Practice1")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon
                      sx={{ color: "success.main", fontSize: 20 }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("observationGuide.section3Practice2")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon
                      sx={{ color: "success.main", fontSize: 20 }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("observationGuide.section3Practice3")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon
                      sx={{ color: "success.main", fontSize: 20 }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("observationGuide.section3Practice4")}
                  />
                </ListItem>
              </List>
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 1,
                  bgcolor: alpha(theme.palette.warning.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <WarningIcon sx={{ fontSize: 20, color: "warning.main" }} />
                  {t("observationGuide.important")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mt: 1, color: "text.secondary" }}
                >
                  {t("observationGuide.section3Warning")}
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Section 4: Step 3 - File Attachments */}
          <Accordion
            sx={{
              mb: 2,
              boxShadow: 1,
              "&:before": { display: "none" },
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08) },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <AttachFileIcon sx={{ color: "primary.main" }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("observationGuide.section4Title")}
                </Typography>
                <Chip
                  label={t("observationGuide.optional")}
                  size="small"
                  sx={{ ml: 1 }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {t("observationGuide.section4Text")}
              </Typography>
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  borderRadius: 1,
                  bgcolor: alpha(theme.palette.info.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  {t("observationGuide.section4FileTypesTitle")}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  📎 JPG/JPEG, PNG, GIF, PDF •{" "}
                  {t("observationGuide.section4FileLimit")}
                </Typography>
              </Box>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CloudUploadIcon sx={{ color: "primary.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("observationGuide.section4Method1")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CloudUploadIcon sx={{ color: "primary.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("observationGuide.section4Method2")}
                  />
                </ListItem>
              </List>
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 1,
                  bgcolor: alpha(theme.palette.info.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <TipsIcon sx={{ fontSize: 20, color: "info.main" }} />
                  {t("observationGuide.proTip")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mt: 1, color: "text.secondary" }}
                >
                  {t("observationGuide.section4ProTip")}
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Section 5: Step 4 - Additional Information */}
          <Accordion
            sx={{
              mb: 2,
              boxShadow: 1,
              "&:before": { display: "none" },
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08) },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <InfoIcon sx={{ color: "primary.main" }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("observationGuide.section5Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {t("observationGuide.section5Text")}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                {t("observationGuide.section5DynamicFieldsTitle")}:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <InfoIcon sx={{ color: "primary.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("observationGuide.section5FieldType1")}
                    secondary={t(
                      "observationGuide.section5FieldType1Description"
                    )}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <InfoIcon sx={{ color: "primary.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("observationGuide.section5FieldType2")}
                    secondary={t(
                      "observationGuide.section5FieldType2Description"
                    )}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <InfoIcon sx={{ color: "primary.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("observationGuide.section5FieldType3")}
                    secondary={t(
                      "observationGuide.section5FieldType3Description"
                    )}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItem>
              </List>
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 1,
                  bgcolor: alpha(theme.palette.warning.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <WarningIcon sx={{ fontSize: 20, color: "warning.main" }} />
                  {t("observationGuide.important")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mt: 1, color: "text.secondary" }}
                >
                  {t("observationGuide.section5Warning")}
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Section 6: Step 5 - Review & Submit */}
          <Accordion
            sx={{
              mb: 2,
              boxShadow: 1,
              "&:before": { display: "none" },
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08) },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <RateReviewIcon sx={{ color: "primary.main" }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("observationGuide.section6Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {t("observationGuide.section6Text")}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                {t("observationGuide.section6ProcessTitle")}:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="1" size="small" color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("observationGuide.section6Process1")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="2" size="small" color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("observationGuide.section6Process2")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="3" size="small" color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("observationGuide.section6Process3")}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Chip label="4" size="small" color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("observationGuide.section6Process4")}
                  />
                </ListItem>
              </List>
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 1,
                  bgcolor: alpha(theme.palette.success.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <CheckCircleIcon
                    sx={{ fontSize: 20, color: "success.main" }}
                  />
                  {t("observationGuide.onSuccess")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mt: 1, color: "text.secondary" }}
                >
                  {t("observationGuide.section6Success")}
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Section 7: Best Practices */}
          <Accordion
            sx={{
              mb: 2,
              boxShadow: 1,
              "&:before": { display: "none" },
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: alpha(theme.palette.success.main, 0.05),
                "&:hover": { bgcolor: alpha(theme.palette.success.main, 0.08) },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <StarIcon sx={{ color: "success.main" }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t("observationGuide.section7Title")}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: "success.main" }} />
                  </ListItemIcon>
                  <ListItemText primary={t("observationGuide.section7Step1")} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: "success.main" }} />
                  </ListItemIcon>
                  <ListItemText primary={t("observationGuide.section7Step2")} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: "success.main" }} />
                  </ListItemIcon>
                  <ListItemText primary={t("observationGuide.section7Step3")} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: "success.main" }} />
                  </ListItemIcon>
                  <ListItemText primary={t("observationGuide.section7Step4")} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: "success.main" }} />
                  </ListItemIcon>
                  <ListItemText primary={t("observationGuide.section7Step5")} />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          {/* Quick Reference */}
          <Box
            sx={{
              mt: 3,
              p: 3,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.success.main, 0.05),
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, mb: 2, color: "success.main" }}
            >
              {t("observationGuide.quickRefTitle")}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <Chip label="1" size="small" color="success" />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t("observationGuide.quickRef1Title")}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    {t("observationGuide.quickRef1Text")}
                  </Typography>
                </Box>
              </Box>
              <Divider />
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <Chip label="2" size="small" color="success" />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t("observationGuide.quickRef2Title")}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    {t("observationGuide.quickRef2Text")}
                  </Typography>
                </Box>
              </Box>
              <Divider />
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <Chip label="3" size="small" color="success" />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t("observationGuide.quickRef3Title")}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    {t("observationGuide.quickRef3Text")}
                  </Typography>
                </Box>
              </Box>
              <Divider />
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <Chip label="4" size="small" color="success" />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t("observationGuide.quickRef4Title")}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    {t("observationGuide.quickRef4Text")}
                  </Typography>
                </Box>
              </Box>
              <Divider />
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <Chip label="5" size="small" color="success" />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t("observationGuide.quickRef5Title")}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    {t("observationGuide.quickRef5Text")}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          bgcolor: alpha(theme.palette.background.default, 0.5),
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          size="large"
          sx={{
            borderRadius: 2,
            px: 4,
            py: 1.5,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "1rem",
          }}
          aria-label={t("observationGuide.closeAria")}
        >
          {t("observationGuide.closeButton")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ObservationGuideDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ObservationGuideDialog;
